'use client';

import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';

type TextFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel';
  required?: boolean;
  control: Control<T>;
  error?: string;
  colSpan?: 1 | 2;
};

const inputBase =
  'h-9 w-full rounded-lg border border-gray-200 px-3 text-base font-normal text-gray-600 outline-none transition-all placeholder:text-gray-400 hover:bg-gray-50/50 hover:border-royal-300 hover:shadow-sm focus:-translate-y-px focus:ring-2 focus:ring-theme-primary-light focus:ring-offset-2 focus:ring-offset-white';

const inputError =
  'border-red-400 hover:border-red-400 focus:ring-red-300 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.08)]';

const inputOk = 'focus:shadow-[0_0_0_3px_rgba(37,35,146,0.08)]';

export function TextField<T extends FieldValues>({
  name,
  label,
  placeholder,
  type = 'text',
  required,
  control,
  error,
}: TextFieldProps<T>) {
  const fieldId = `field-${String(name).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center">
        <label
          htmlFor={fieldId}
          className="text-royal-500 text-base font-semibold"
        >
          {label}
        </label>
        {required && (
          <span className="ml-1 text-xs font-semibold text-red-500">*</span>
        )}
      </span>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <input
            id={fieldId}
            {...field}
            type={type}
            placeholder={placeholder}
            className={`${inputBase} ${error ? inputError : inputOk}`}
          />
        )}
      />
      {error && (
        <span role="alert" className="mt-0.5 text-[10px] text-red-500">
          {error}
        </span>
      )}
    </div>
  );
}
